type TelegramClipboardWindow = Window & {
  Telegram?: {
    WebApp?: {
      platform?: string;
    };
  };
};

type LegacyCopyDocument = {
  execCommand?(command: string, showUi?: boolean, value?: string): boolean;
};

function isAndroidWebView(): boolean {
  const telegramPlatform = (window as TelegramClipboardWindow).Telegram?.WebApp?.platform?.toLowerCase() ?? "";
  return telegramPlatform.includes("android") || navigator.userAgent.toLowerCase().includes("android");
}

function copyWithSelection(text: string): boolean {
  const legacyDocument = document as unknown as LegacyCopyDocument;
  if (typeof legacyDocument.execCommand !== "function") return false;
  const previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.readOnly = true;
  textarea.tabIndex = -1;
  textarea.setAttribute("aria-hidden", "true");
  Object.assign(textarea.style, {
    position: "fixed",
    top: "0",
    left: "0",
    width: "2px",
    height: "2px",
    padding: "0",
    border: "0",
    outline: "0",
    opacity: "0",
    pointerEvents: "none",
  });
  document.body.append(textarea);
  textarea.focus({ preventScroll: true });
  textarea.select();
  textarea.setSelectionRange(0, text.length);
  let copied: boolean;
  try {
    copied = legacyDocument.execCommand("copy");
  } catch {
    copied = false;
  }
  textarea.remove();
  previousFocus?.focus({ preventScroll: true });
  return copied;
}

export async function copyText(text: string): Promise<boolean> {
  // Android Telegram WebViews may expose an unusable or missing Clipboard API.
  // The selection command must run synchronously inside the original gesture.
  const preferSelection = isAndroidWebView();
  if (preferSelection && copyWithSelection(text)) return true;

  if (typeof navigator.clipboard?.writeText === "function") {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      // Fall through to the synchronous-compatible browser path.
    }
  }

  return preferSelection ? false : copyWithSelection(text);
}
